<script setup lang="ts">
import { dashboardApiClient } from '@api-client/dashboard';
import type { ResType } from '@api-client/typeUtils';
import { currentWebsiteId } from '@partner-spa-vue/stores/MyWebsitesStore';
import { onMounted, ref } from 'vue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-vue-next';
import { toast } from '@/ui-plus/sonner';
import StoreDetails from '../StoreDetails.vue';

type SupportedStoresResponse = ResType<typeof dashboardApiClient.website.supported_stores.list[':websiteId']['$get']>["data"];

const supportedStores = ref<SupportedStoresResponse['stores']>([]);
const loading = ref(false);
const hasMore = ref(false);
const limit = ref(10);
const cursorStack = ref<string[]>([]);
const currentCursor = ref<string | undefined>(undefined);
const searchQuery = ref('');

const loadStores = async (cursor?: string) => {
    loading.value = true;
    const { data, error } = await (await dashboardApiClient.website.supported_stores.list[':websiteId'].$get({
        param: { websiteId: currentWebsiteId.value ?? '' },
        query: {
            limit: String(limit.value),
            cursor,
            search: searchQuery.value || undefined
        }
    })).json();

    if (error) {
        console.error(error);
        toast.error('Failed to load stores');
    } else if (data) {
        supportedStores.value = data.stores;
        hasMore.value = data.pagination.hasMore;
        currentCursor.value = cursor;
    }
    loading.value = false;
};

const handleSearch = () => {
    cursorStack.value = [];
    loadStores(undefined);
};

const handleNext = () => {
    const nextCursor = supportedStores.value[supportedStores.value.length - 1]?.id;
    if (!nextCursor) return;
    if (currentCursor.value !== undefined) {
        cursorStack.value.push(currentCursor.value);
    }
    loadStores(nextCursor);
};

const handlePrev = () => {
    const prevCursor = cursorStack.value.pop();
    loadStores(prevCursor);
};

onMounted(async () => {
    loadStores();
});


</script>

<template>
    <div class="space-y-6">

        <div class="flex gap-2">
            <div class="relative flex-1">
                <Search class="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input type="search"
                       placeholder="Search stores..."
                       v-model="searchQuery"
                       class="pl-8"
                       @keyup.enter="handleSearch" />
            </div>
            <Button @click="handleSearch">Search</Button>
        </div>

        <div v-if="!loading"
             class="space-y-4">
            <div v-for="store in supportedStores"
                 :key="store.id"
                 class="flex items-center justify-between p-4 border rounded-lg">
                <StoreDetails v-if="currentWebsiteId"
                              :store="store"
                              :currentWebsiteId="currentWebsiteId!" />
            </div>

            <div v-if="supportedStores.length === 0"
                 class="p-8 text-center text-muted-foreground">
                No stores found that you are supporting as a support partner.
            </div>
        </div>

        <div v-else
             class="p-8 text-center text-muted-foreground">
            Loading stores...
        </div>

        <div v-if="cursorStack.length > 0 || hasMore"
             class="flex justify-center gap-2 mt-4">
            <Button variant="outline"
                    :disabled="cursorStack.length === 0"
                    @click="handlePrev">Previous</Button>
            <Button variant="outline"
                    :disabled="!hasMore"
                    @click="handleNext">Next</Button>
        </div>

    </div>
</template>